<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ArrowPathIcon, CheckCircleIcon, ChevronRightIcon, EllipsisHorizontalIcon, MagnifyingGlassIcon, PencilSquareIcon,
  NoSymbolIcon, PlusIcon, ShieldCheckIcon, TrashIcon, UserIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import Pts2ControllerNav from '../components/pts2/Pts2ControllerNav.vue'
import Pts2DiagnosticsStrip from '../components/pts2/Pts2DiagnosticsStrip.vue'
import { session } from '../session.js'
import { getConfigurationObservation } from '../pts2/configurationHistory.js'
import { configurationPumpIds, tagInventoryMessage } from '../pts2/controllerTabPresentation.js'
import { getControllerSummary } from '../pts2/controllers.js'
import { confirmOperatorTagOperation, createOperatorTagPreview, deleteOperatorTagPreview, getOperatorTag,
  listOperatorTags, updateOperatorTagPreview } from '../pts2/operatorTags.js'
import '../pts2/controllers.css'

const route = useRoute()
const controllerId = computed(() => Number(route.params.controllerId))
const controller = ref(null)
const registry = ref(null)
const configuration = ref(null)
const configurationError = ref('')
const previewOnly = computed(() => session.applications.find((app) => app.id === 'PTS2')?.availability !== 'READY')
const inventoryComplete = computed(() => registry.value?.inventory?.state === 'COMPLETE')
const canWrite = computed(() => !previewOnly.value && !configurationError.value && !!configuration.value && !loading.value && !busy.value)
const selected = ref(null)
const loading = ref(true)
const busy = ref(false)
const error = ref('')
const search = ref('')
const state = ref('ALL')
const pump = ref('ALL')
const editor = ref(null)
const confirmation = ref(null)
const loadingMore = ref(false)
const draft = reactive({ tagValue: '', name: '', valid: true, allowedPumpIds: [] })
let request

const stationId = computed(() => controller.value?.stationId ?? null)
const stationName = computed(() => controller.value?.stationDisplayName ?? `АЗС ${stationId.value ?? '—'}`)
const visibleTags = computed(() => (registry.value?.items ?? []).filter((item) => pump.value === 'ALL'
  || item.allowedPumpIds.includes(Number(pump.value))))
const pumpOptions = computed(() => configurationPumpIds(configuration.value))

function formatDate(value) {
  const date = value ? new Date(value) : null
  return date && Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat('uk-UA', { dateStyle: 'short', timeStyle: 'short' }).format(date) : '—'
}

function tagState(tag) {
  if (tag.remotePresence === 'UNKNOWN') return { label: 'Результат невідомий', tone: 'unknown' }
  return tag.valid ? { label: 'Активний', tone: 'active' } : { label: 'Заблокований', tone: 'blocked' }
}

function operationLabel(kind) {
  return ({ TAG_CREATE: 'Створення', TAG_UPDATE: 'Редагування', TAG_DELETE: 'Видалення' })[kind] ?? kind
}

async function load({ preserveSelection = true } = {}) {
  request?.abort()
  const active = new AbortController()
  request = active
  loading.value = true
  error.value = ''
  try {
    const summary = await getControllerSummary(controllerId.value, { signal: active.signal })
    if (active.signal.aborted) return
    controller.value = summary
    configuration.value = null
    configurationError.value = ''
    try {
      if (controller.value.configurationObservationId) {
        const observed = await getConfigurationObservation(controller.value.stationId, controllerId.value,
          controller.value.configurationObservationId, { signal: active.signal })
        if (active.signal.aborted) return
        configuration.value = observed.configuration
      } else configurationError.value = 'Конфігурацію ПРК ще не отримано.'
    } catch (failure) {
      if (failure?.name === 'AbortError') return
      configurationError.value = 'Не вдалося прочитати конфігурацію ПРК. Запис тегів недоступний.'
    }
    const result = await listOperatorTags(controller.value.stationId, controllerId.value,
      { search: search.value, state: state.value, signal: active.signal })
    if (active.signal.aborted) return
    registry.value = result
    const selectedId = preserveSelection ? selected.value?.tag.id : null
    const next = registry.value.items.find((item) => item.id === selectedId) ?? registry.value.items[0]
    selected.value = next ? await getOperatorTag(controller.value.stationId, controllerId.value, next.id,
      { signal: active.signal }) : null
  } catch (failure) {
    if (failure?.name !== 'AbortError') error.value = 'Операторські теги недоступні. Перевірте доступ і повторіть спробу.'
  } finally {
    if (!active.signal.aborted) loading.value = false
  }
}

async function selectTag(tag) {
  if (busy.value || selected.value?.tag.id === tag.id) return
  busy.value = true
  try {
    selected.value = await getOperatorTag(stationId.value, controllerId.value, tag.id, { signal: request?.signal })
  } catch (failure) {
    if (failure?.name !== 'AbortError') error.value = 'Не вдалося відкрити деталі тега.'
  } finally {
    busy.value = false
  }
}

async function loadMore() {
  if (!registry.value?.hasMore || !registry.value.nextCursor || loadingMore.value) return
  loadingMore.value = true
  error.value = ''
  try {
    const next = await listOperatorTags(stationId.value, controllerId.value,
      { search: search.value, state: state.value, cursor: registry.value.nextCursor, limit: 50,
        signal: request?.signal })
    registry.value = { ...next, items: [...registry.value.items, ...next.items] }
  } catch (failure) {
    if (failure?.name !== 'AbortError') error.value = 'Не вдалося завантажити наступну сторінку тегів.'
  } finally {
    loadingMore.value = false
  }
}

function openEditor(mode) {
  if (!canWrite.value) return
  editor.value = mode
  draft.tagValue = ''
  draft.name = mode === 'edit' ? selected.value.tag.name : ''
  draft.valid = mode === 'edit' ? selected.value.tag.valid : true
  draft.allowedPumpIds = mode === 'edit' ? [...selected.value.tag.allowedPumpIds] : []
  nextTick(() => document.querySelector('.pts2-tag-dialog input')?.focus())
}

function closeEditor() {
  editor.value = null
  draft.tagValue = ''
}

function togglePump(id) {
  draft.allowedPumpIds = draft.allowedPumpIds.includes(id)
    ? draft.allowedPumpIds.filter((value) => value !== id) : [...draft.allowedPumpIds, id].sort((a, b) => a - b)
}

async function previewEditor() {
  if (!canWrite.value || !draft.name || editor.value === 'create' && !draft.tagValue) return
  busy.value = true
  error.value = ''
  try {
    const preview = editor.value === 'create'
      ? await createOperatorTagPreview(stationId.value, controllerId.value, registry.value.registryVersion, draft)
      : await updateOperatorTagPreview(stationId.value, controllerId.value, selected.value.tag, draft)
    confirmation.value = { kind: editor.value === 'create' ? 'create' : 'edit', preview,
      title: editor.value === 'create' ? 'Додати операторський тег?' : 'Зберегти зміни тега?',
      description: 'Перед відправленням сервер повторно перевірить права та вихідний стан контролера.' }
    closeEditor()
  } catch (failure) {
    error.value = failure?.code === 'tag_baseline_pending'
      ? 'Сервер читає поточний стан тега. Повторіть підтвердження через кілька секунд.'
      : 'Не вдалося підготувати зміну тега. Дані контролеру не надсилалися.'
  } finally {
    busy.value = false
  }
}

async function requestAction(kind) {
  if (!selected.value || !canWrite.value) return
  if (kind === 'edit') return openEditor('edit')
  busy.value = true
  error.value = ''
  try {
    const tag = selected.value.tag
    const preview = kind === 'delete'
      ? await deleteOperatorTagPreview(stationId.value, controllerId.value, tag)
      : await updateOperatorTagPreview(stationId.value, controllerId.value, tag,
        { name: tag.name, valid: !tag.valid, allowedPumpIds: tag.allowedPumpIds })
    confirmation.value = {
      kind,
      preview,
      title: kind === 'delete' ? `Видалити тег «${tag.name}»?`
        : tag.valid ? `Заблокувати тег «${tag.name}»?` : `Розблокувати тег «${tag.name}»?`,
      description: kind === 'delete' ? 'Тег буде видалено з контролера після окремої перевірки стану. Цю дію не можна скасувати автоматично.'
        : 'Після підтвердження сервер змінить лише цей службовий тег і перевірить результат читанням.',
    }
  } catch (failure) {
    error.value = 'Не вдалося підготувати операцію. Дані контролеру не надсилалися.'
  } finally {
    busy.value = false
  }
}

async function confirmAction() {
  if (!confirmation.value || !canWrite.value) return
  busy.value = true
  error.value = ''
  try {
    await confirmOperatorTagOperation(stationId.value, controllerId.value,
      confirmation.value.preview.previewId, globalThis.crypto.randomUUID())
    confirmation.value = null
    await load({ preserveSelection: true })
  } catch (failure) {
    confirmation.value = null
    error.value = failure?.status === 503
      ? 'Команди тегів залишаються заблокованими до hardware/release gate. Чернетку не було повторно надіслано.'
      : 'Не вдалося підтвердити операцію. Автоматичне повторне надсилання не виконується.'
  } finally {
    busy.value = false
  }
}

let searchTimer
watch([search, state], () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => load({ preserveSelection: false }), 250)
})
watch(controllerId, () => load({ preserveSelection: false }))
onMounted(() => load({ preserveSelection: false }))
onBeforeUnmount(() => { request?.abort(); clearTimeout(searchTimer); draft.tagValue = '' })
</script>

<template>
  <section class="pc-page pc-detail pts2-tags-page" aria-labelledby="pts2-tags-title">
    <nav class="pc-breadcrumb" aria-label="Навігаційний шлях"><RouterLink :to="{ name: 'pts2-controllers' }">Контролери</RouterLink><ChevronRightIcon aria-hidden="true" /><span>{{ controller?.displayName ?? 'Операторські теги' }}</span></nav>
    <div v-if="loading" class="pc-table-loading" role="status">Завантажуємо операторські теги…</div>
    <p v-if="!loading && error" class="pc-notice" role="alert">{{ error }}</p>
    <template v-else-if="controller && registry">
      <header class="pc-heading pc-detail-heading pts2-page-heading"><div><div class="pts2-heading-line"><h1 id="pts2-tags-title">Операторські теги</h1><span class="pc-state" :class="controller.connectionFreshness === 'FRESH' ? 'fresh' : 'unknown'"><i></i>{{ controller.connectionFreshness === 'FRESH' ? 'Онлайн' : 'Стан невідомий' }}</span></div><p>{{ controller.stableCode }} · {{ stationName }} · Останні дані: {{ formatDate(controller.lastSeenAt) }}</p></div><div class="pc-heading-actions"><button class="pc-secondary" type="button" @click="load"><ArrowPathIcon aria-hidden="true" />Оновити</button></div></header>
      <Pts2DiagnosticsStrip :snapshot="controller.diagnostics" :summary="controller" :failed="false" />
      <Pts2ControllerNav :controller-id="controllerId" active="operator-tags" />

      <p v-if="previewOnly" class="pc-notice" role="status">Доступний перегляд тегів. Додавання, редагування, блокування й видалення вимкнені до завершення перевірок готовності PTS-2.</p>
      <p class="pc-notice" :role="registry.inventory.state === 'PARTIAL' ? 'alert' : 'status'">{{ tagInventoryMessage(registry.inventory) }}</p>
      <p v-if="configurationError" class="pc-notice" role="alert">{{ configurationError }}</p>
      <p v-if="registry.authorizationMode.state === 'UNKNOWN'" class="pts2-tag-mode-note"><ShieldCheckIcon aria-hidden="true" /><span><strong>Режим авторизації ПРК не підтверджено</strong> Теги дозволяють запуск ПРК лише коли цей режим увімкнено на контролері. Інтерфейс не робить припущень без перевіреної конфігурації.</span></p>

      <section class="pts2-surface pts2-tags-list">
        <header><h2>Операторські теги</h2><button class="pc-primary" type="button" :disabled="!canWrite" @click="openEditor('create')"><PlusIcon />Додати тег</button></header>
        <div class="pts2-tag-filters"><label class="pts2-search"><MagnifyingGlassIcon /><input v-model="search" placeholder="Пошук за назвою тега або маскованим значенням" /></label><select v-model="state" aria-label="Стан тега"><option value="ALL">Усі стани</option><option value="ACTIVE">Активні</option><option value="BLOCKED">Заблоковані</option><option value="UNKNOWN">Результат невідомий</option></select><select v-model="pump" aria-label="Дозволена ПРК"><option value="ALL">Усі ПРК</option><option v-for="id in pumpOptions" :key="id" :value="String(id)">ПРК {{ id }}</option></select></div>
        <div class="pts2-table-scroll"><table><thead><tr><th>Назва тега</th><th>Маскований тег</th><th>Стан</th><th>Дозволені ПРК</th><th>Остання перевірка</th><th><span class="sr-only">Дії</span></th></tr></thead><tbody>
          <tr v-for="tag in visibleTags" :key="tag.id" :class="{ selected: selected?.tag.id === tag.id }" @click="selectTag(tag)"><td><strong>{{ tag.name }}</strong></td><td>{{ tag.maskedTag }}</td><td><span class="pts2-tag-state" :class="tagState(tag).tone"><i></i>{{ tagState(tag).label }}</span></td><td>{{ tag.allowedPumpIds.length ? `ПРК ${tag.allowedPumpIds.join(', ')}` : 'Без доступу' }}</td><td>{{ formatDate(tag.observedAt) }}</td><td><button type="button" class="pts2-row-menu" :aria-label="`Відкрити ${tag.name}`"><EllipsisHorizontalIcon /></button></td></tr>
        </tbody></table></div><p v-if="!visibleTags.length" class="pc-table-empty">{{ inventoryComplete ? 'Операторських тегів за вибраними фільтрами немає.' : 'Підтверджений список тегів ще не отримано.' }}</p><button v-if="registry.hasMore" class="pc-secondary pts2-more" type="button" :disabled="loadingMore" @click="loadMore">{{ loadingMore ? 'Завантажуємо…' : 'Показати ще' }}</button><p class="pts2-list-count">Показано: {{ visibleTags.length }}</p>
      </section>

      <section v-if="selected" class="pts2-surface pts2-tag-detail">
        <header><h2>Тег «{{ selected.tag.name }}»</h2><button type="button" class="pts2-icon-button" aria-label="Закрити деталі" @click="selected = null"><XMarkIcon /></button></header>
        <div class="pts2-tag-detail-grid"><div class="pts2-tag-properties">
          <dl><div><dt>Назва тега</dt><dd>{{ selected.tag.name }} <button class="pc-secondary compact" type="button" :disabled="!canWrite" @click="requestAction('edit')"><PencilSquareIcon />Редагувати тег</button></dd></div><div><dt>Маскований тег</dt><dd>{{ selected.tag.maskedTag }}</dd></div><div><dt>Стан</dt><dd><span class="pts2-tag-state" :class="tagState(selected.tag).tone"><i></i>{{ tagState(selected.tag).label }}</span></dd></div><div><dt>Дозволені ПРК</dt><dd>{{ selected.tag.allowedPumpIds.length ? `ПРК ${selected.tag.allowedPumpIds.join(', ')}` : 'Без доступу' }} <button class="pc-secondary compact" type="button" :disabled="!canWrite" @click="requestAction('edit')"><UserIcon />Змінити доступ до ПРК</button></dd></div><div><dt>Остання перевірка</dt><dd>{{ formatDate(selected.tag.observedAt) }}<small v-if="selected.tag.remotePresence === 'PRESENT'"><CheckCircleIcon />Успішна перевірка</small></dd></div></dl>
          <div class="pts2-tag-danger"><button class="pts2-block-button" type="button" :disabled="!canWrite" @click="requestAction('block')"><NoSymbolIcon />{{ selected.tag.valid ? 'Заблокувати тег' : 'Розблокувати тег' }}</button><button class="pts2-delete-button" type="button" :disabled="!canWrite" @click="requestAction('delete')"><TrashIcon />Видалити тег</button></div>
        </div><div class="pts2-tag-operations"><h3>Історія операцій</h3><table><thead><tr><th>Час</th><th>Подія</th><th>Деталі</th></tr></thead><tbody><tr v-for="operation in selected.operations" :key="operation.id"><td>{{ formatDate(operation.createdAt) }}</td><td>{{ operationLabel(operation.kind) }}</td><td>{{ operation.state === 'VERIFIED' ? 'Успішно' : operation.state }}</td></tr></tbody></table><p v-if="!selected.operations.length" class="pc-table-empty">Підтверджених операцій ще немає.</p></div></div>
      </section>
    </template>

    <div v-if="editor" class="pts2-modal-backdrop" role="presentation" @mousedown.self="closeEditor"><section class="pts2-tag-dialog" role="dialog" aria-modal="true" :aria-labelledby="'tag-editor-title'"><header><h2 id="tag-editor-title">{{ editor === 'create' ? 'Додати операторський тег' : 'Редагувати тег' }}</h2><button class="pts2-icon-button" type="button" aria-label="Закрити" @click="closeEditor"><XMarkIcon /></button></header><label v-if="editor === 'create'">UID тега<input v-model.trim="draft.tagValue" maxlength="32" autocomplete="off" placeholder="Наприклад, 001122AABBCC" /></label><small v-if="editor === 'create'">UID шифрується на сервері й не буде показаний повторно.</small><label>Назва тега<input v-model="draft.name" maxlength="20" placeholder="Наприклад, Іваненко Сергій" /></label><fieldset><legend>Дозволені ПРК</legend><label v-for="id in pumpOptions" :key="id"><input type="checkbox" :checked="draft.allowedPumpIds.includes(id)" @change="togglePump(id)" />ПРК {{ id }}</label></fieldset><footer><button class="pc-secondary" type="button" @click="closeEditor">Скасувати</button><button class="pc-primary" type="button" :disabled="busy" @click="previewEditor">Переглянути зміну</button></footer></section></div>

    <div v-if="confirmation" class="pts2-modal-backdrop" role="presentation"><section class="pts2-tag-dialog confirm" role="alertdialog" aria-modal="true" aria-labelledby="tag-confirmation-title" aria-describedby="tag-confirmation-description"><h2 id="tag-confirmation-title">{{ confirmation.title }}</h2><p id="tag-confirmation-description">{{ confirmation.description }}</p><footer><button class="pc-secondary" type="button" @click="confirmation = null">Скасувати</button><button :class="confirmation.kind === 'delete' ? 'pts2-delete-button' : 'pc-primary'" type="button" :disabled="busy" @click="confirmAction">{{ busy ? 'Перевіряємо…' : 'Підтвердити' }}</button></footer></section></div>
  </section>
</template>
